import { useEffect, useMemo, useState } from 'react';
import { BarChart3, Download, Package, Star } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { MapleMarketApp } from '@/data/mapleMarketApps';
import { supabase } from '@/integrations/supabase/client';

interface AnalyticsPanelProps {
  products: MapleMarketApp[];
  isSiteAdmin: boolean;
}

interface InstallRow {
  sku: string;
  action: string;
  created_at: string;
}

export default function AnalyticsPanel({ products, isSiteAdmin }: AnalyticsPanelProps) {
  const [installRows, setInstallRows] = useState<InstallRow[]>([]);
  const [loadingInstalls, setLoadingInstalls] = useState(isSiteAdmin);

  useEffect(() => {
    if (!isSiteAdmin) {
      setInstallRows([]);
      setLoadingInstalls(false);
      return;
    }

    let active = true;

    const loadInstalls = async () => {
      setLoadingInstalls(true);
      const { data } = await (supabase.from('marketplace_installs') as any)
        .select('sku, action, created_at')
        .order('created_at', { ascending: false })
        .limit(1000);

      if (active) {
        setInstallRows((data ?? []) as InstallRow[]);
        setLoadingInstalls(false);
      }
    };

    void loadInstalls();

    return () => {
      active = false;
    };
  }, [isSiteAdmin]);

  const totalReviews = useMemo(
    () => products.reduce((total, product) => total + product.reviews, 0),
    [products]
  );

  const averageRating = useMemo(() => {
    if (products.length === 0) {
      return 0;
    }

    return products.reduce((total, product) => total + product.rating, 0) / products.length;
  }, [products]);

  const installMetrics = useMemo(() => {
    const installEvents = installRows.filter((row) => row.action === 'install');
    const last7Days = Date.now() - 1000 * 60 * 60 * 24 * 7;
    const installsLast7Days = installEvents.filter((row) => new Date(row.created_at).getTime() >= last7Days).length;
    const installCountBySku = installEvents.reduce<Record<string, number>>((accumulator, row) => {
      accumulator[row.sku] = (accumulator[row.sku] ?? 0) + 1;
      return accumulator;
    }, {});

    const topInstalled = [...products]
      .map((product) => ({
        product,
        installs: installCountBySku[product.sku] ?? 0,
      }))
      .sort((left, right) => right.installs - left.installs)
      .slice(0, 5);

    return {
      totalInstallEvents: installEvents.length,
      installsLast7Days,
      uniqueInstalledProducts: Object.keys(installCountBySku).length,
      topInstalled,
    };
  }, [installRows, products]);

  const categoryBreakdown = useMemo(() => {
    const counts = products.reduce<Record<string, number>>((accumulator, product) => {
      accumulator[product.category] = (accumulator[product.category] ?? 0) + 1;
      return accumulator;
    }, {});

    return Object.entries(counts).sort((left, right) => right[1] - left[1]);
  }, [products]);

  const topReviewed = useMemo(
    () => [...products].sort((left, right) => right.reviews - left.reviews).slice(0, 5),
    [products]
  );

  const statCards = [
    {
      title: 'Visible apps',
      value: products.length,
      description: 'Products currently in your admin scope',
      icon: Package,
    },
    {
      title: 'Total reviews',
      value: totalReviews.toLocaleString(),
      description: 'Combined review volume across visible products',
      icon: BarChart3,
    },
    {
      title: 'Average rating',
      value: averageRating ? averageRating.toFixed(1) : '0.0',
      description: 'Average marketplace rating',
      icon: Star,
    },
    {
      title: 'Install events',
      value: isSiteAdmin ? installMetrics.totalInstallEvents.toLocaleString() : '—',
      description: isSiteAdmin ? 'Recorded install events from marketplace flows' : 'Site admin access required',
      icon: Download,
    },
  ];

  return (
    <div className="space-y-6 p-6 lg:p-8">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Analytics</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Review product performance, ratings, category spread, and install activity available to your role.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {statCards.map((card) => (
          <Card key={card.title}>
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
              <div>
                <CardDescription>{card.title}</CardDescription>
                <CardTitle className="mt-2 text-3xl">{card.value}</CardTitle>
              </div>
              <div className="rounded-xl bg-primary/10 p-2 text-primary">
                <card.icon className="h-4 w-4" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{card.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Top reviewed apps</CardTitle>
            <CardDescription>Products with the strongest review volume in your current scope.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {topReviewed.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">
                No analytics are available yet.
              </div>
            ) : (
              topReviewed.map((product) => (
                <div key={product.id} className="flex items-center gap-4 rounded-xl border border-border p-3">
                  <img src={product.image} alt={product.name} className="h-12 w-12 rounded-lg object-cover" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-foreground">{product.name}</div>
                    <div className="text-sm text-muted-foreground">{product.category}</div>
                  </div>
                  <div className="text-right text-sm">
                    <div className="font-medium text-foreground">{product.reviews.toLocaleString()}</div>
                    <div className="text-muted-foreground">★ {product.rating.toFixed(1)}</div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Category distribution</CardTitle>
            <CardDescription>Breakdown of visible products by marketplace category.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {categoryBreakdown.map(([category, count]) => {
              const share = products.length === 0 ? 0 : Math.round((count / products.length) * 100);
              return (
                <div key={category} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-foreground">{category}</span>
                    <span className="text-muted-foreground">{count} · {share}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted">
                    <div className="h-2 rounded-full bg-primary" style={{ width: `${share}%` }} />
                  </div>
                </div>
              );
            })}
            {categoryBreakdown.length === 0 && (
              <div className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">
                Add products to see category analytics.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Install activity</CardTitle>
          <CardDescription>
            {isSiteAdmin
              ? 'Marketplace install events currently visible to site admins.'
              : 'Install events are currently available to site admins only; owners and admins still see listing-level analytics above.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {isSiteAdmin ? (
            loadingInstalls ? (
              <div className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">
                Loading install activity…
              </div>
            ) : installMetrics.topInstalled.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">
                No install events have been recorded yet.
              </div>
            ) : (
              <>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="rounded-xl border border-border p-4">
                    <div className="text-sm text-muted-foreground">Total install events</div>
                    <div className="mt-1 text-2xl font-semibold text-foreground">{installMetrics.totalInstallEvents.toLocaleString()}</div>
                  </div>
                  <div className="rounded-xl border border-border p-4">
                    <div className="text-sm text-muted-foreground">Last 7 days</div>
                    <div className="mt-1 text-2xl font-semibold text-foreground">{installMetrics.installsLast7Days.toLocaleString()}</div>
                  </div>
                  <div className="rounded-xl border border-border p-4">
                    <div className="text-sm text-muted-foreground">Products installed</div>
                    <div className="mt-1 text-2xl font-semibold text-foreground">{installMetrics.uniqueInstalledProducts.toLocaleString()}</div>
                  </div>
                </div>

                <div className="space-y-3">
                  {installMetrics.topInstalled.map(({ product, installs }) => (
                    <div key={product.id} className="flex items-center justify-between rounded-xl border border-border p-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <img src={product.image} alt={product.name} className="h-10 w-10 rounded-lg object-cover" />
                        <div className="min-w-0">
                          <div className="truncate font-medium text-foreground">{product.name}</div>
                          <div className="text-sm text-muted-foreground">{product.sku}</div>
                        </div>
                      </div>
                      <Badge variant="secondary">{installs} installs</Badge>
                    </div>
                  ))}
                </div>
              </>
            )
          ) : (
            <div className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">
              Listing analytics are active for your products. Install event reporting will appear here automatically when per-product install visibility is expanded beyond site admins.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
